import { type PropsWithChildren, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fromEvent, tap } from 'rxjs';

import { ToolContextMenu } from '@/component/Tool';
import { TOOL_CONST } from '@/constant';
import { pcdManager } from '@/manager';
import {
  VButton,
  VFlex,
  cn,
  createSafeContext,
  useFlag,
  useSubscriptions,
} from '@/package';
import { usePcdRenderingStore } from '@/store';

import ToolPcdCanvas from './ToolPcdCanvas';
import ToolPcdCanvasBoxList from './ToolPcdCanvasBoxList';
import ToolPcdCanvasBoxTrackID from './ToolPcdCanvasBoxTrackID';
import ToolPcdCanvasPcdViewer from './ToolPcdCanvasPcdViewer';
import ToolPcdCanvasZoomController from './ToolPcdCanvasZoomController';
import type { ToolPcdCanvasContextValue, ToolPcdCanvasProps } from './type';

export const ToolPcdCanvasContext =
  createSafeContext<ToolPcdCanvasContextValue>(
    {} as ToolPcdCanvasContextValue,
    'ToolPcdCanvasContext',
  );

export const ToolPcdCanvasContextProvider = ({
  children,
  viewMode,
  renderShortcuts,
  ...rest
}: PropsWithChildren<
  ToolPcdCanvasProps & Pick<ToolPcdCanvasContextValue, 'viewMode'>
>) => {
  const { t } = useTranslation();
  const isViewMode = useMemo(
    () =>
      Object.values(TOOL_CONST.TOOL_CAMERA_VIEW_MODE).reduce(
        (acc, val) => ({
          ...acc,
          [val]: val === viewMode,
        }),
        {} as {
          [K in Lowercase<TOOL_CONST.TOOL_CAMERA_VIEW_MODE>]: boolean;
        },
      ),
    [viewMode],
  );
  const pcdIndex = usePcdRenderingStore(state => state.pcdIndex);
  const [renderedIndex, setRenderedIndex] = useState(-1);
  const { value: visibleDefaultPcd, toggle: toggleDefaultPcd } = useFlag(false);
  const $elRef = useRef<HTMLDivElement>(null);

  const getCanvasElement = () => $elRef.current?.querySelector('canvas');

  useSubscriptions(
    () => [
      fromEvent(pcdManager, 'afterChangeGeometry')
        .pipe(tap(() => setRenderedIndex(pcdIndex)))
        .subscribe(),
    ],
    [pcdIndex],
  );

  return (
    <ToolPcdCanvasContext.Provider
      value={{
        backgroundPcd: {
          visible: visibleDefaultPcd,
          toggle: toggleDefaultPcd,
        },
        enabledFullScreen: rest.enabledFullScreen,
        enabledDragBox: rest.enabledDragBox,
        initCameraOptions: rest.initCameraOptions,
        isViewMode,
        viewMode,
        getCanvasElement,
      }}
    >
      <VFlex
        ref={$elRef}
        className={`relative h-full bg-black`}
        direction="col"
      >
        {!isViewMode.default && (
          <>
            <VFlex items="center">
              <ToolPcdCanvasZoomController />

              <div className="w-fit pr-2">
                <VButton
                  className={cn({ 'w-max': true })}
                  size="md"
                  onClick={toggleDefaultPcd}
                >
                  {visibleDefaultPcd
                    ? t('tool.turnOffBGPcd.podogynium')
                    : t('tool.turnOnBGPcd.niobite')}
                </VButton>
              </div>
            </VFlex>

            <VFlex
              items="center"
              justify="center"
              className="absolute bottom-4 z-10 w-full text-body-md text-white"
            >
              {renderShortcuts?.(isViewMode)}
            </VFlex>
          </>
        )}
        <div className="h-full flex-1 bg-black" data-view-text={viewMode}>
          <ToolPcdCanvas {...rest}>
            <ToolPcdCanvasBoxList renderedIndex={renderedIndex} />
            {isViewMode.default && <ToolPcdCanvasBoxTrackID />}
            <ToolPcdCanvasPcdViewer renderedIndex={renderedIndex} />
            {children}
          </ToolPcdCanvas>

          {isViewMode.default && (
            <ToolContextMenu
              parentRef={$elRef as React.RefObject<HTMLElement>}
            />
          )}
        </div>
      </VFlex>
    </ToolPcdCanvasContext.Provider>
  );
};
